import { useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import axios from "axios";
import { captureException } from "@sentry/react";
import { useSearchParams } from "react-router";
import { toast } from "react-toastify";
import { useAppData } from "store/appData";
import { useNavBarTitle } from "components/NavBar";
import {
  DATASET_API,
  type Dataset,
  type DatasetKind,
  type DefectDataset,
} from "./api";
import DatasetGrid from "./DatasetGrid";
import { useDatasetViewerSettings } from "./hooks/useDatasetViewerSettings";
import t from "./translations";

const TAB_PARAM = "tab";
const DEFECT_TAB = "defect";

function parseKind(value: string | null): DatasetKind {
  return value === DEFECT_TAB ? "defect" : "object";
}

export default function DatasetViewer() {
  const { projects, objectTypes } = useAppData();
  const [searchParams, setSearchParams] = useSearchParams();
  const kind = parseKind(searchParams.get(TAB_PARAM));

  const [datasetsByKind, setDatasetsByKind] = useState<{
    object: Dataset[] | null;
    defect: DefectDataset[] | null;
  }>({ object: null, defect: null });
  const [loadingByKind, setLoadingByKind] = useState<
    Record<DatasetKind, boolean>
  >({ object: false, defect: false });
  const inFlightRef = useRef<Record<DatasetKind, AbortController | null>>({
    object: null,
    defect: null,
  });

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<number[]>([]);
  const [hideInaccessible, setHideInaccessible] = useState(false);
  const [showDeprecated, setShowDeprecated] = useState(false);

  const { projectNameDisplay } = useDatasetViewerSettings();

  useNavBarTitle(t.PageTitle);

  const datasets = datasetsByKind[kind] ?? [];
  const loading = loadingByKind[kind];

  const usedTypeIds = useMemo(
    () => new Set(datasets.flatMap((d) => d.type_list)),
    [datasets]
  );

  const objectTypeMap = useMemo(
    () =>
      new Map(Object.values(objectTypes).map((ot) => [ot.id, ot.en_name])),
    [objectTypes]
  );

  const typeFilterOptions = useMemo(
    () =>
      Object.values(objectTypes)
        .filter((ot) => usedTypeIds.has(ot.id))
        .map((ot) => ot.id),
    [objectTypes, usedTypeIds]
  );

  const filteredDatasets = useMemo(() => {
    const projectIds = new Set(Object.values(projects).map((p) => p.id));
    let result = datasets;
    if (search.trim()) {
      const lower = search.toLowerCase();
      result = result.filter((d) => d.name.toLowerCase().includes(lower));
    }
    if (typeFilter.length > 0) {
      result = result.filter((d) =>
        typeFilter.every((id) => d.type_list.includes(id))
      );
    }
    if (hideInaccessible) {
      result = result.filter((d) =>
        d.approved_customers.some((id) => projectIds.has(id))
      );
    }
    if (!showDeprecated) {
      result = result.filter((d) => !d.deprecated);
    }
    return result;
  }, [datasets, search, typeFilter, hideInaccessible, showDeprecated, projects]);

  useEffect(() => {
    if (datasetsByKind[kind] !== null || inFlightRef.current[kind]) return;

    const controller = new AbortController();
    inFlightRef.current[kind] = controller;
    setLoadingByKind((prev) => ({ ...prev, [kind]: true }));

    (async () => {
      try {
        const rows = await DATASET_API[kind].list();
        if (controller.signal.aborted) return;
        setDatasetsByKind((prev) => ({ ...prev, [kind]: rows }));
      } catch (error) {
        if (axios.isCancel(error)) return;
        captureException(error, {
          tags: { feature: `datasetViewer.fetch.${kind}` },
        });
        toast.error(t.FetchError);
      } finally {
        if (inFlightRef.current[kind] === controller) {
          inFlightRef.current[kind] = null;
        }
        if (!controller.signal.aborted) {
          setLoadingByKind((prev) => ({ ...prev, [kind]: false }));
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [kind, datasetsByKind]);

  const handleTabChange = (_: unknown, value: DatasetKind) => {
    setSearch("");
    setTypeFilter([]);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === "defect") next.set(TAB_PARAM, DEFECT_TAB);
        else next.delete(TAB_PARAM);
        return next;
      },
      { replace: true }
    );
  };

  return (
    <Stack sx={{ p: 2, height: "100%", gap: 2 }}>
      <Tabs
        value={kind}
        onChange={handleTabChange}
        data-testid="dataset-kind-tabs"
      >
        <Tab
          label={t.ObjectDatasetsTab}
          value="object"
          data-testid="dataset-kind-tab.object"
        />
        <Tab
          label={t.DefectDatasetsTab}
          value="defect"
          data-testid="dataset-kind-tab.defect"
        />
      </Tabs>
      <Stack sx={{ flexDirection: "row", gap: 2, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder={t.SearchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            htmlInput: { "data-testid": "dataset-search-input" },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
          sx={{ minWidth: 300 }}
        />
        <Autocomplete
          multiple
          size="small"
          options={typeFilterOptions}
          getOptionLabel={(id) => objectTypeMap.get(id) ?? String(id)}
          value={typeFilter}
          onChange={(_, value) => setTypeFilter(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={
                typeFilter.length === 0 ? t.FilterByType : undefined
              }
            />
          )}
          renderValue={(value, getItemProps) =>
            value.map((id, index) => {
              const { key, ...itemProps } = getItemProps({ index });
              return (
                <Chip
                  {...itemProps}
                  key={key}
                  label={objectTypeMap.get(id) ?? id}
                  size="small"
                />
              );
            })
          }
          sx={{ minWidth: 300 }}
          data-testid="type-filter-input"
          disableCloseOnSelect
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={hideInaccessible}
              onChange={(e) => setHideInaccessible(e.target.checked)}
              size="small"
            />
          }
          label={t.HideInaccessible}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showDeprecated}
              onChange={(e) => setShowDeprecated(e.target.checked)}
              size="small"
            />
          }
          label={t.ShowDeprecated}
        />
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <DatasetGrid
          rows={filteredDatasets}
          loading={loading}
          kind={kind}
          objectTypeMap={objectTypeMap}
          projectNameDisplay={projectNameDisplay}
        />
      </Box>
    </Stack>
  );
}
